import { z } from 'zod';
import { DomainError, InvalidStatusTransitionError } from './errors.js';

export const CAMPAIGN_PERIOD_STATUSES = ['draft', 'active', 'paused', 'closed'] as const;
export type CampaignPeriodStatus = (typeof CAMPAIGN_PERIOD_STATUSES)[number];
export const campaignPeriodStatusSchema = z.enum(CAMPAIGN_PERIOD_STATUSES);

const CAMPAIGN_PERIOD_TRANSITIONS: Record<CampaignPeriodStatus, readonly CampaignPeriodStatus[]> = {
  draft: ['active'],
  active: ['paused', 'closed'],
  paused: ['active', 'closed'],
  closed: [],
};

export function canTransitionPeriod(from: CampaignPeriodStatus, to: CampaignPeriodStatus): boolean {
  return CAMPAIGN_PERIOD_TRANSITIONS[from].includes(to);
}

export function assertPeriodTransition(from: CampaignPeriodStatus, to: CampaignPeriodStatus): void {
  if (!canTransitionPeriod(from, to)) {
    throw new InvalidStatusTransitionError('campaignPeriod.status', from, to);
  }
}

export function nextPeriodStatuses(from: CampaignPeriodStatus): readonly CampaignPeriodStatus[] {
  return CAMPAIGN_PERIOD_TRANSITIONS[from];
}

export function isTerminalPeriodStatus(status: CampaignPeriodStatus): boolean {
  return CAMPAIGN_PERIOD_TRANSITIONS[status].length === 0;
}

export const CAMPAIGN_PERIOD_FIELDS = [
  'seasonYear',
  'startsAt',
  'endsAt',
  'goal',
  'overflowPolicy',
  'portalVisibility',
  'showWhenClosed',
  'acceptsBackdatedUntil',
] as const;
export type CampaignPeriodField = (typeof CAMPAIGN_PERIOD_FIELDS)[number];

const RUNNING_PERIOD_FIELDS: readonly CampaignPeriodField[] = [
  'endsAt',
  'goal',
  'overflowPolicy',
  'portalVisibility',
  'showWhenClosed',
];

/**
 * What an administrator may still change at each point of the lifecycle. A
 * draft is free. A running season keeps its year and its opening date, because
 * donations already sit inside them, but may be extended, re-targeted or
 * hidden. A closed season is a record: only whether the portal still shows it
 * and how long a late paper form is accepted remain — see
 * docs/decisions/0016-campaign-period-lifecycle.md.
 */
const EDITABLE_PERIOD_FIELDS: Record<CampaignPeriodStatus, readonly CampaignPeriodField[]> = {
  draft: CAMPAIGN_PERIOD_FIELDS.filter((field) => field !== 'acceptsBackdatedUntil'),
  active: RUNNING_PERIOD_FIELDS,
  paused: RUNNING_PERIOD_FIELDS,
  closed: ['showWhenClosed', 'acceptsBackdatedUntil'],
};

export function editablePeriodFields(status: CampaignPeriodStatus): readonly CampaignPeriodField[] {
  return EDITABLE_PERIOD_FIELDS[status];
}

export function assertPeriodFieldsEditable(
  status: CampaignPeriodStatus,
  fields: readonly CampaignPeriodField[],
): void {
  const editable = EDITABLE_PERIOD_FIELDS[status];
  const locked = fields.filter((field) => !editable.includes(field));
  if (locked.length === 0) return;
  throw new DomainError('CONFLICT', `Period fields are locked while ${status}`, {
    reason: 'CAMPAIGN_PERIOD_FIELD_LOCKED',
    status,
    fields: locked,
  });
}

const PRICES_EDITABLE: Record<CampaignPeriodStatus, boolean> = {
  draft: true,
  active: true,
  paused: true,
  closed: false,
};

export function periodPricesEditable(status: CampaignPeriodStatus): boolean {
  return PRICES_EDITABLE[status];
}

export function assertPeriodPricesEditable(status: CampaignPeriodStatus): void {
  if (PRICES_EDITABLE[status]) return;
  throw new DomainError('CONFLICT', 'A closed period keeps the prices it closed with', {
    reason: 'CAMPAIGN_PERIOD_PRICES_LOCKED',
    status,
  });
}

export const BACKDATED_ENTRY_DAYS = 7;

/**
 * How long a paper form filled in on the last day may still be submitted after
 * the season closes, when the administrator does not say — close time plus
 * seven days.
 */
export function defaultAcceptsBackdatedUntil(closedAt: Date): Date {
  return new Date(closedAt.getTime() + BACKDATED_ENTRY_DAYS * 24 * 60 * 60 * 1000);
}

const ASSIGNMENTS_EDITABLE: Record<CampaignPeriodStatus, boolean> = {
  draft: true,
  active: true,
  paused: true,
  closed: false,
};

export function periodAssignmentsEditable(status: CampaignPeriodStatus): boolean {
  return ASSIGNMENTS_EDITABLE[status];
}

export function assertPeriodAssignmentsEditable(status: CampaignPeriodStatus): void {
  if (ASSIGNMENTS_EDITABLE[status]) return;
  throw new DomainError('CONFLICT', 'A closed period keeps the assignments it closed with', {
    reason: 'CAMPAIGN_PERIOD_ASSIGNMENTS_LOCKED',
    status,
  });
}

/**
 * One season yurt as the gate reads it: whether it is active, and how many
 * active primary users the in-force staff assignment for the campaign at its
 * institution names (docs/decisions/0095).
 */
export type AssignmentReadiness = {
  assignmentId: string;
  isActive: boolean;
  activePrimaryStaff: number;
};

export function assignmentsWithoutPrimary(
  assignments: readonly AssignmentReadiness[],
): readonly string[] {
  return assignments
    .filter((assignment) => assignment.isActive && assignment.activePrimaryStaff === 0)
    .map((assignment) => assignment.assignmentId);
}

/**
 * A season may leave `draft` only when every **active** assignment's
 * institution is staffed for the campaign by an in-force staff assignment
 * naming at least one primary user who is active — docs/decisions/0035, whose
 * managers became those staff users in docs/decisions/0095. An inactive
 * assignment is not executing anything and is ignored.
 *
 * A period with no assignments at all may activate: assignment governs field
 * fulfillment, not collection, and many campaigns collect without it — see
 * docs/decisions/0015-campaign-ownership-scope.md.
 */
export function assertMayLeaveDraft(assignments: readonly AssignmentReadiness[]): void {
  const missing = assignmentsWithoutPrimary(assignments);
  if (missing.length === 0) return;
  throw new DomainError('CONFLICT', 'An active assignment has no primary staff user', {
    reason: 'CAMPAIGN_PERIOD_ASSIGNMENT_WITHOUT_PRIMARY',
    count: missing.length,
    assignmentIds: missing,
  });
}
